import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, Save, UserPlus, Trash2, Mail, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/AppLayout";
import { UpgradeGate } from "@/components/UpgradeGate";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { INDUSTRIES, STATES } from "@/lib/constants";
import { toast } from "sonner";
import { NotificationSettings } from "@/components/NotificationSettings";
import { EmailSettings } from "@/components/EmailSettings";

const ENTITY = ["LLC", "Sole Proprietorship", "C Corporation", "S Corporation", "Nonprofit (501c3)", "Partnership"];
const SPAN = { 3: "md:col-span-3", 6: "md:col-span-6" };
const FIELDS = [
  ["company_name", "Legal company name", 6], ["website", "Website", 3], ["phone", "Phone", 3],
  ["address", "Business address", 6], ["city", "City", 3], ["founded_year", "Year founded", 3],
  ["employees", "Employees", 3], ["annual_revenue", "Annual revenue", 3],
];

function ProfileForm() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/workspace").then((r) => setP(r.data.profile || {})); }, []);
  if (!p) return <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />;
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });
  const sel = (k, opts) => (
    <Select value={p[k] || ""} onValueChange={(v) => setP({ ...p, [k]: v })}>
      <SelectTrigger className="mt-1.5" data-testid={`profile-${k}-select`}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
  const save = async () => {
    setBusy(true);
    try { await api.put("/workspace/profile", { profile: p }); toast.success("Business profile saved"); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="profile-form">
      {FIELDS.slice(0, 1).map(([k, l, s]) => <div key={k} className={SPAN[s]}><Label>{l}</Label><Input className="mt-1.5" value={p[k] || ""} onChange={set(k)} data-testid={`profile-${k}-input`} /></div>)}
      <div className="md:col-span-3"><Label>Entity type</Label>{sel("entity_type", ENTITY)}</div>
      <div className="md:col-span-3"><Label>Industry</Label>{sel("industry", INDUSTRIES)}</div>
      <div className="md:col-span-3"><Label>State</Label>{sel("state", STATES)}</div>
      {FIELDS.slice(1).map(([k, l, s]) => <div key={k} className={SPAN[s]}><Label>{l}</Label><Input className="mt-1.5" value={p[k] || ""} onChange={set(k)} data-testid={`profile-${k}-input`} /></div>)}
      <div className="md:col-span-12"><Label>Mission / what you do</Label><Textarea rows={3} className="mt-1.5" value={p.mission || ""} onChange={set("mission")} data-testid="profile-mission-input" /></div>
      <div className="md:col-span-12"><Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="profile-save-button">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save profile</Button></div>
    </div>
  );
}

function Team() {
  const { user, hasPkg } = useAuth();
  const [t, setT] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/team").then((r) => setT(r.data));
  useEffect(() => { load(); }, []);
  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/team/invites", { email });
      toast.success(data.email_sent ? `Invitation emailed to ${email}` : "Invitation created (email could not be sent)");
      setEmail("");
      load();
    } catch (e2) { toast.error(formatErr(e2)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { if (!window.confirm("Remove this person from the workspace?")) return; await api.delete(`/team/members/${id}`); if (id === user.user_id) window.location.href = "/dashboard"; else load(); };
  const cancel = async (id) => { await api.delete(`/team/invites/${id}`); load(); };
  if (!t) return <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />;
  const owner = t.role === "owner";
  return (
    <div className="space-y-4">
      {owner && !hasPkg("suite") ? (
        <UpgradeGate pkg="suite" title="Invite your team">Multi-user workspaces and in-document comments are included in the Full Verify Suite. Invite co-founders, assistants and partners to collaborate on proposals and documents.</UpgradeGate>
      ) : owner && (
        <form onSubmit={invite} className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col sm:flex-row gap-3" data-testid="team-invite-form">
          <Input type="email" required placeholder="teammate@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="team-invite-email-input" />
          <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 shrink-0" data-testid="team-invite-submit-button">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}Send invite</Button>
        </form>
      )}
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="team-members-list">
        {t.members.map((m) => (
          <div key={m.user_id} className="p-4 flex items-center gap-3" data-testid={`team-member-${m.user_id}`}>
            <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center font-semibold text-sm">{m.name[0]}</div>
            <div className="flex-1 min-w-0"><div className="font-semibold text-sm text-slate-900">{m.name}</div><div className="text-xs text-slate-500 truncate">{m.email}</div></div>
            {m.role === "owner" ? <span className="text-xs text-amber-700 flex items-center gap-1"><Crown className="h-3 w-3" />Owner</span> : <span className="text-xs text-slate-500">Member</span>}
            {m.role !== "owner" && (owner || m.user_id === user.user_id) && <button onClick={() => remove(m.user_id)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`team-remove-${m.user_id}`}><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
        {t.invites.map((i) => (
          <div key={i.invite_id} className="p-4 flex items-center gap-3 bg-slate-50" data-testid={`team-invite-${i.invite_id}`}>
            <Mail className="h-4 w-4 text-slate-400" />
            <div className="flex-1 text-sm text-slate-600">{i.email} <span className="text-xs text-slate-400">· invite pending</span></div>
            <button onClick={() => cancel(i.invite_id)} className="text-xs text-slate-500 hover:text-red-600" data-testid={`team-cancel-invite-${i.invite_id}`}>Cancel</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PendingInvites() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/invites/mine").then((r) => setItems(r.data)); }, []);
  if (!items.length) return null;
  const accept = async (token) => {
    try { await api.post(`/invites/${token}/accept`); window.location.href = "/dashboard"; } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 mb-8 space-y-2" data-testid="pending-invites">
      {items.map((i) => (
        <div key={i.invite_id} className="flex items-center justify-between gap-3">
          <span className="text-sm text-slate-700"><b>{i.invited_by}</b> invited you to <b>{i.workspace_name}</b></span>
          <Button size="sm" onClick={() => accept(i.token)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`accept-invite-${i.invite_id}`}>Accept</Button>
        </div>
      ))}
    </div>
  );
}

export default function Company() {
  const [nk, setNk] = useState(0);
  const { hash, key } = useLocation();
  useEffect(() => { if (hash === "#notifications") setTimeout(() => document.getElementById("notifications")?.scrollIntoView({ behavior: "smooth" }), 400); }, [hash, key]);
  return (
    <div>
      <PageHeader eyebrow="Company & Team" title="Business profile">Used to personalise proposals, generated documents, calendar deadlines and your Ready-to-Apply score.</PageHeader>
      <PendingInvites />
      <ProfileForm />
      <div className="mt-6"><EmailSettings onAllOff={() => setNk((k) => k + 1)} /></div>
      <div className="mt-6"><NotificationSettings key={nk} /></div>
      <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Team workspace</h2>
      <Team />
    </div>
  );
}
